import { createContext, useContext, useEffect, useRef, useState } from 'react';
import graph from '@utils/graph';
import IProvider from '@models/IProvider';
import db from '@utils/db';
import { isInIframe } from '@utils/tokenRefresh';
import { useStore } from '@store/appStore';
import { AuthService } from '@libs/teams-auth';
import { shallow } from 'zustand/shallow';
import persistentStore from '@store/persistentStore';
import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { msalConfig, silentRequest } from '@libs/teams-auth/auth-service/authConfig';
import { InteractionStatus, PublicClientApplication } from '@azure/msal-browser';
import moment from 'moment/moment';
import { useI18n } from 'i18next-react';
import OnScreenDebugger from '@utils/OnScreenDebuuger';
import { useRouter } from 'next/router';
import pkceChallenge from "pkce-challenge";
import { useAuthBase } from './useAuthBase';
const defaultUser = { displayName: '', photo: '', groups: [] };

type IAuthProvider = {
  user: any;
  error: any;
  login: (e?: any, popup?: boolean) => any;
  logout: () => any;
  getProfilePic: (res: string) => Promise<string>;
  getProfile: () => Promise<any>;
  groups: string[];
  getId: () => any;
  getType: () => any;
  getStudentId: () => any;
  loginMitId: (url) => any;
  getTokenTeams: (string) => any;
  signInAgain?: () => Promise<void>;
};

const AuthContext = createContext({
  user: null, // User account from MSAL
  error: null, // Error from MSAL
  login: () => { }, //login function
  logout: () => { }, //logout function
  getProfilePic: null, // getting profile picture function
  getProfile: null, //get user profile function
  groups: [], //user groups
} as IAuthProvider);

//const APP_ID = process.env.ENV === 'DEV' ? 'b1f251b1-e0c9-436d-a0f6-b967039697af' : '3741153e-2490-463c-aff7-5283fb1bd1b4';
const APP_ID = '3741153e-2490-463c-aff7-5283fb1bd1b4';

const msalInstance = new PublicClientApplication(msalConfig);

// useMsal()/useIsAuthenticated() below need MsalProvider as an ancestor, so it
// has to wrap this component from the outside rather than be rendered inside it.
export function DesktopAuthProvider({ children }: IProvider) {
  return (
    <MsalProvider instance={msalInstance}>
      <DesktopAuthProviderInner>{children}</DesktopAuthProviderInner>
    </MsalProvider>
  );
}

function DesktopAuthProviderInner({ children }: IProvider) {
  const mobile = false;
  const isDemo = process.env.NEXT_PUBLIC_IS_DEMO === 'true';
  const isMultiLogin = false
  const teams = process.env.NEXT_PUBLIC_IS_TEAMS === 'true';
  const isLoggedIn = useIsAuthenticated();
  const { instance, inProgress } = useMsal();
  const {
    demo, error, seterror, router,
    authenticator, setAuthenticator, loggingIn, setlogginIn,
    client, setClientData, setSettings, settings, setWhoIsLoggedIn, setTutorial, setGroups, groups,
    token, settoken, setMenuOpen, reset, setTenantId,
    systemSettings, ids, setIds, user, setUser,
    initialRegistrationTypes, setInitialRegistrationTypes, hideAbsenceMenu, setHideAbsenceMenu,
    setCaseWorkerId, setAlerts, caseWorkerEmail, setCaseWorkerEmail, schoolName,
    setSchoolName, setSelectedCaseworker, wondeTags, setWondeTags, multilogin, setMultiLogin,
    setOtherUser, otherUser, whoIsLoggedIn, setSchools, setGuardianStudents, guardianStudents,
    filterDate, schools, setNotificationCounter,
    selectedSemester, kohortsAvailable, setKohortsAvailable, setPopupLogin,
    setActive, active, apiUrl, setApiUrl,
    getId, getStudentId, getType, getCookie, setCookie, delete_cookie, loginMitId, getTokenTeams,
    getDemoData, assignGroups,
    awaitSettings, getMitidToken, assignGroupsMitId,
    checkToken, signInAgain,
  } = useAuthBase({ mobile, isDemo, teams, isMultiLogin, getProfile, instance });

  useEffect(() => {
    if (isLoggedIn) {
      let accounts = instance.getAllAccounts();
      if (accounts && accounts.length > 0) setUser(accounts[0]);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (isMultiLogin && router.pathname === "/login" && router.isReady) {
      getMitidToken(window.location.origin)
    }
  }, [router.isReady])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (settings) {
        if (settings.DEMO_ENV && !demo) {
          getDemoData();
          return;
        }

        // MSAL is still processing a pending redirect response (e.g. the #code= it lands back
        // with after loginRedirect()) while inProgress !== None. Calling loginRedirect() again
        // here before that finishes interrupts it - handleRedirectPromise() then resolves to
        // null and the login that was already in flight is lost. Settings used to resolve this
        // effect slowly enough (a real getClientSettings network call) that this race rarely
        // surfaced; it no longer does, so this needs to wait for MSAL explicitly instead.
        if (!user && !settings.DEMO_ENV && router.pathname !== "/login" && inProgress === InteractionStatus.None) {
          if (isMultiLogin) {
            let loginType = sessionStorage.getItem('loginType')
            if (loginType !== "ms" && isMultiLogin) setMultiLogin(1)
          }
          else {
            handleLogin();
          }
        }
        return;
      }
      awaitSettings();
    }
  }, [settings, inProgress]);

  useEffect(() => {
    if (isLoggedIn) {
      if (!client && !token && !groups) {
        let accounts = instance.getAllAccounts();
        if (accounts && accounts.length > 0) setUser(accounts[0]);
      }
    }
  }, []);

  // The login sequence (group lookup, user info, MergeLogin, tutorial...) - once per signed-in user and
  // school. `token` is only a precondition: since tokens are renewed in the background (tokenRefresh),
  // it changes about hourly, and re-running all of this on every renewal re-did the login mid-session
  // (and showed the login error page whenever the Graph lookup failed at that moment).
  const groupsAssignedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!user) {
      groupsAssignedFor.current = null;
      return;
    }
    if (client && token) {
      const key = (user.localAccountId ?? user.username) + '|' + client.client_id;
      if (groupsAssignedFor.current === key) return;
      groupsAssignedFor.current = key;
      assignGroups();
    }
  }, [client, token, user]);

  useEffect(() => {
    if (isMultiLogin && client && user && user.nin) {
      assignGroupsMitId();
    }
  }, [client, token, user]);

  async function getProfilePic(resolution: string = '240x240'): Promise<string> {
    if (settings.DEMO === 'true') {
      return null;
    }
    const hasToken = await checkToken();
    if (!hasToken) return;
    return graph.getPhoto(token, resolution);
  }

  async function getProfile(): Promise<any> {
    const hasToken = await checkToken();
    if (!hasToken) return;
    return graph.getSelf(token);
  }

  async function logout() {
    let loginType = sessionStorage.getItem('loginType')
    let u = JSON.parse(JSON.stringify(user));
    sessionStorage.clear();
    sessionStorage.setItem('logout', '1');
    reset();
    setUser(null);
    delete_cookie('msal_username');
    setMenuOpen(false);
    localStorage.clear();
    setlogginIn(false);
    await router.push('/');
    if (!settings.TEAMS && settings.TEAMS !== 'true') {
      if (loginType !== "mitId") instance.logoutRedirect({ postLogoutRedirectUri: '/' })
      else {
        if (schools.length > 1 && u != null) {
          setOtherUser(u)
          setMultiLogin(0)
          delete_cookie('school')
        }
        else {
          setOtherUser(null)
          setMultiLogin(1)
          delete_cookie('user')
        }
      }
    }
    else {
      if (!teams) instance.logoutRedirect({ postLogoutRedirectUri: '/' });
      else authenticator.logout();
    }
  }

  async function login(popupLogin?: boolean) {
    if (popupLogin) {
      let username = getCookie('msal_username');
      if (username && username.length > 0) {
        instance.loginPopup({
          ...silentRequest,
          loginHint: username,
        }).catch(err => {
          sessionStorage.removeItem('loggingIn');  
          setPopupLogin(true);
          db.Log('Login popup failed. Error: ' + err);
        });
      }
      else {
        instance.loginPopup({ ...silentRequest }).catch(err => {
          sessionStorage.removeItem('loggingIn');
          setPopupLogin(true);
          db.Log('Login popup failed. Error: ' + err);
        });
      }
      return;
    }

    try {
      // Call the MSAL auth helper to login
      if (!teams) {
        if (!isLoggedIn && isInIframe()) {
          // Embedded in another page: MSAL refuses redirects in an iframe, so go straight to the
          // login screen's popup button instead of failing a redirect first.
          setPopupLogin(true);
          return;
        }
        if (sessionStorage.getItem('loggingIn') !== '1' && !isLoggedIn) {
          sessionStorage.setItem('loggingIn', '1');
          let username = getCookie('msal_username');
          if (username && username.length > 0) {
            db.Log('Username cookie found, logging in with hint: ' + username);
            await instance.loginRedirect({
              ...silentRequest,
              loginHint: username,
            }).catch(error => {
              db.Log('Login redirect failed, falling back to login popup. Error: ' + error);
              instance.loginPopup({
                ...silentRequest,
                loginHint: username,
              }).catch(err => {
                sessionStorage.removeItem('loggingIn');
                setPopupLogin(true);
                db.Log('Login popup failed. Error: ' + err);
              });
            });
          }
          else {
            db.Log('No username cookie found, logging in without hint');
            await instance.loginRedirect({ ...silentRequest }).catch(error => {
              db.Log('Login redirect failed, falling back to login popup. Error: ' + error);
              instance.loginPopup({ ...silentRequest }).catch(err => {
                setPopupLogin(true);
                db.Log('Login popup failed. Error: ' + err);
              });
            });
          }
        }
      } else await authenticator.login();
    } catch (err) {
      //OnScreenDebugger.instance.log('AUTH: MSAL ERROR' + err);
      //seterror(err.toString());
    }
  }

  async function handleLogin() {
    if (!settings) {
      await awaitSettings();
    }
    if (settings && teams) {
      const auth = new AuthService({
        auth: {
          clientId: APP_ID,
        },
      });

      const url = new URL(window.location.href);
      if (url.hash) {
        await auth.handleLoginRedirect().catch((e) => console.error('LOGIN_ERROR:', e));
      } else {
        const outsideTeams = await auth.login().then(
          () => false,
          (e) => {
            console.error('LOGIN_ERROR:', e);
            // teams-js found no Teams window around the page: the Teams build was opened in an
            // ordinary browser, where it can't sign in - say so instead of a generic login error.
            return /No Parent window/i.test(String(e?.message ?? e));
          },
        );
        if (outsideTeams) {
          seterror({ code: 413, title: 'teams-only-title', message: 'teams-only-description' });
          return;
        }
      }
      setAuthenticator(auth);
    } else {
      if (!sessionStorage.getItem('logginIn') && !sessionStorage.getItem('logout')) await login();
    }
  }


  return (
    <AuthContext.Provider
      value={{
        user,
        error,
        login,
        logout,
        getProfilePic,
        groups,
        getProfile,
        getId,
        getType,
        getStudentId,
        loginMitId,
        getTokenTeams,
        signInAgain,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuthentication = () => useContext(AuthContext);
